import { useState } from "react";
import styles from "./PackagesModal.module.scss";
import { PackageInfo, PackagesNames, packagesTitle } from "./packages";
import { useRequest } from "hooks/useRequest";
import { getCampaignPaymentTokenThunk } from "core/orderAd/orderAd.thunk";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CloseIcon,
  OutLineTick,
} from "@bale/ui";
import { strings } from "intl/fa";
import { formatNumberToPersian, formatStrings } from "utils/utils";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import Modal from "components/commons/modal/Modal";
import { safeRedirect } from "utils/payment";
import { PackageIcons } from "./PackagesModal";
import { useTheme } from "@emotion/react";
import classNames from "classnames";
import { CampaignPackageType } from "core/orderAd/orderAd.entity";
import { useNeedAuth } from "hooks/useNeedAuth";
import { useAppDispatch } from "utils/store";
import { AppPage, NavigationActions } from "core/navigation/navigation.slice";
import { useMyPanel } from "hooks/useMyPanel";
import { AccountType } from "core/auth/auth.entity";
import { toast } from "react-toastify";

type PackageItemProps = {
  package: PackageInfo;
  closeModal?: VoidFunction;
  isRecommended?: boolean;
  isFestival?: boolean;
};

export const PackageItem = ({
  package: packageData,
  closeModal,
  isFestival,
  isRecommended,
}: PackageItemProps) => {
  const theme = useTheme();
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const { sendRequest: getCampaignPaymentToken } = useRequest(
    getCampaignPaymentTokenThunk
  );
  const dispatch = useAppDispatch();
  const { needsAuth, isAuthenticating } = useNeedAuth();
  const myPanel = useMyPanel();

  const handleBuying = () => {
    const { onSuccess: safeOnSuccess, onError: safeOnError } = safeRedirect();
    getCampaignPaymentToken({
      package_type: packageData.packageType,
      onSuccess: safeOnSuccess,
      onError: safeOnError,
    });
  };
  const buyClickHandler = () => {
    if (isAuthenticating) {
      toast.info(strings.common.error.auth_pending);
      return;
    }
    if (needsAuth) {
      closeModal?.();
      dispatch(NavigationActions.setAppPage(AppPage.Invoicing));

      if (myPanel.type === AccountType.Personal) {
        dispatch(NavigationActions.setNavigationCallback(handleBuying));
      }
      return;
    }
    handleBuying();
  };
  const finalPrice =
    packageData.price_rial -
    (packageData.discounted_percent * packageData.price_rial) / 100;
  const hadDiscount = packageData.discounted_percent > 0;

  return (
    <>
      <div className={styles.CardWrapper} id={packageData.name}>
        {isRecommended && (
          <div className={styles.CardHeader}>
            {strings.packages.modal.package_item.most_favorite}
          </div>
        )}
        <div
          className={classNames(styles.ItemCard, {
            [styles.ItemCard_Rec]: isRecommended,
          })}
        >
          <div className={styles.ItemHeader}>
            {isFestival ? (
              <p className={styles.festival_title}>🍉</p>
            ) : (
              PackageIcons[packageData.name as PackagesNames]
            )}
            <p className={styles.Title}>
              {isFestival
                ? strings.side_bar.yalda_package.title_no_icon
                : packagesTitle[packageData.packageType]}
            </p>
            <p className={styles.Desc}>
              {isFestival
                ? strings.side_bar.yalda_package.description
                : packageData.desc}
            </p>
          </div>
          <div className={styles.ItemFeatures}>
            <div className={styles.FeatItem}>
              <OutLineTick size={24} color={theme.colors.dark[400]} />
              <div className={styles.FeatText}>
                {strings.packages.modal.package_item.campaign_feat}{" "}
              </div>
            </div>
            <div className={styles.FeatItem}>
              <OutLineTick size={24} color={theme.colors.dark[400]} />
              <div className={styles.FeatText}>
                {formatStrings(
                  strings.packages.modal.package_item.concurrent_count,
                  {
                    count: digitsToHindi(
                      String(packageData.cuncurrent_campaigns)
                    ),
                  }
                )}
              </div>
            </div>
            {packageData.sponsored_count &&
              packageData.sponsored_count !== 0 && (
                <div className={styles.FeatItem}>
                  <OutLineTick size={24} color={theme.colors.dark[400]} />
                  <div className={styles.FeatText}>
                    {formatStrings(
                      strings.packages.modal.package_item.sponroed_count,
                      {
                        count: digitsToHindi(
                          formatNumberToPersian(packageData.sponsored_count)
                        ),
                      }
                    )}
                  </div>
                </div>
              )}
            {packageData.packageType !== CampaignPackageType.CUSTOM && (
              <div className={styles.FeatItem}>
                <OutLineTick size={24} color={theme.colors.dark[400]} />
                <div className={styles.FeatText}>
                  {formatStrings(
                    strings.packages.modal.package_item.overall_view,
                    {
                      count: digitsToHindi(
                        formatNumberToPersian(packageData.view_count)
                      ),
                    }
                  ) +
                    ` (${formatStrings(
                      strings.packages.modal.package_item.daily_view,
                      {
                        count: digitsToHindi(
                          formatNumberToPersian(packageData.daily_view)
                        ),
                      }
                    )})`}
                </div>
              </div>
            )}
            <div className={styles.FeatItem}>
              <OutLineTick size={24} color={theme.colors.dark[400]} />
              <div className={styles.FeatText}>
                {formatStrings(
                  strings.packages.modal.package_item.credit_expire,
                  {
                    days: digitsToHindi(
                      String(packageData.credit_expire_period)
                    ),
                  }
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={classNames(styles.ItemFooter, {
            [styles.ItemFooter_Rec]: isRecommended,
          })}
        >
          <div className={styles.PriceSection}>
            <span className={styles.DiscountContainer}>
              {hadDiscount && (
                <>
                  <p className={styles.DiscountedPrice}>
                    {formatStrings(
                      strings.packages.modal.package_item.price_in_rials,
                      {
                        price: digitsToHindi(
                          toMoneyFormat(String(packageData.price_rial))
                        ),
                      }
                    )}
                  </p>
                  <p className={styles.DiscountPercent}>
                    {digitsToHindi(String(packageData.discounted_percent))}%
                  </p>
                </>
              )}
            </span>
            <span className={styles.Price}>
              {digitsToHindi(toMoneyFormat(String(finalPrice)))}{" "}
              {strings.common.rial}
            </span>
          </div>
          <Button
            size={ButtonSize.Small}
            variant={
              isRecommended ? ButtonVariant.Filled : ButtonVariant.Outlined
            }
            fullWidth
            transparent={!isRecommended}
            color={isRecommended ? "#6900cc" : "#6D00D4"}
            borderWidth={1.5}
            className={classNames(styles.ButtonText, {
              [styles.ButtonText_Rec]: isRecommended,
            })}
            onClick={buyClickHandler}
          >
            {strings.common.button.buy}
          </Button>
        </div>
      </div>
      {showCampaignModal && (
        <Modal
          title={strings.packages.modal.package_item.campaign_title}
          primaryButton={{
            label: strings.common.button.understood,
            onClick: () => setShowCampaignModal(false),
          }}
          onClose={() => setShowCampaignModal(false)}
        >
          <p className={styles.HelpText}>
            {strings.packages.modal.package_item.campaign_help}
          </p>
        </Modal>
      )}
    </>
  );
};
